import React, { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, Bot, CircleHelp, Clock, Crown, Loader2 } from 'lucide-react';
import { HOVER_PANEL_SURFACE } from '@/components/dashboard/primitives';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PopoverArrow } from '@radix-ui/react-popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/components/AuthProvider';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useCurrentSubscription, pendingSubscriptionRefetchInterval } from '@/hooks/useCurrentSubscription';
import { dashboardApi } from '@/lib/api';
import { AI_CREDITS_STATS_QUERY_KEY } from '@/lib/api/aiCreditsGuard';
import { isTrialActuallyExpired } from '@/lib/subscription-utils';
import { cn } from '@/lib/utils';
import { ACCOUNT_BILLING_URL, ACCOUNT_PLANS_URL } from './SidebarAccountFooter';

const AI_CREDITS_DATA = [
  { api: 'Template generation', costPerCall: 0.438 },
  { api: 'Campaign prediction', costPerCall: 0.254 },
  { api: 'Template insights', costPerCall: 0.264 },
  { api: 'Template body refine', costPerCall: 0.134 },
  { api: 'Analytics query', costPerCall: 0.896 },
  { api: 'Guide chat', costPerCall: 0.408 },
  { api: 'Meta ads analyze', costPerCall: 0.288 },
  { api: 'Livechat', costPerCall: 0.36 },
  { api: 'WB AI Agent', costPerCall: 0.60 },
  { api: 'Automation flow', costPerCall: 0.36 },
  { api: 'Analytics', costPerCall: 0.30 },
  { api: 'Chatbot', costPerCall: 0.48 },
  { api: 'Form builder', costPerCall: 0.24 },
  { api: 'Image generator', costPerCall: 0.65 },
];

const METER_SEGMENTS = 26;

function filledSegmentStyle(index: number, filledCount: number): React.CSSProperties {
  const t = filledCount <= 1 ? 0 : index / (filledCount - 1);
  const lightMix = Math.round(50 - t * 50);
  const darkMix = Math.round(30 - t * 30);
  return {
    backgroundImage: [
      'repeating-linear-gradient(-45deg, transparent 0px, transparent 2px, rgba(255, 255, 255, 0.35) 2px, rgba(255, 255, 255, 0.35) 3px)',
      `linear-gradient(color-mix(in srgb, rgb(134, 239, 172) ${lightMix}%, rgb(14, 182, 79)) 0%, color-mix(in srgb, rgb(14, 182, 79) ${darkMix}%, rgb(7, 106, 45)) 100%)`,
    ].join(', '),
  };
}

function formatCredits(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0';
  if (value >= 100) return Math.round(value).toLocaleString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

interface PlanCreditsBarProps {
  onOpenBilling?: () => void;
  className?: string;
  variant?: 'compact' | 'card';
}

export function PlanCreditsBar({ className, variant = 'compact' }: PlanCreditsBarProps) {
  const { user } = useAuth();
  const { interceptedNavigate } = useNavigation();
  const isMobile = useIsMobile();
  const businessAccountContext = useContext(BusinessAccountContext);
  const selectedAccountId = businessAccountContext?.selectedAccountId || null;
  const organizationId = user?.organization_id || (user as any)?.organizationId || null;
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isRegularAdmin = roleRaw === 'ADMIN' && !isSuperAdmin && !isAgency;

  const {
    data: currentSubscription,
    isLoading: subscriptionLoading,
  } = useCurrentSubscription({
    enabled: !!organizationId && isRegularAdmin,
    staleTime: 2 * 60 * 1000,
    // Polls while a payment waits for its webhook (shared with TopNavigation).
    refetchInterval: pendingSubscriptionRefetchInterval,
  });

  const { data: headerStats, isLoading: aiCreditsLoading } = useQuery({
    queryKey: [...AI_CREDITS_STATS_QUERY_KEY, selectedAccountId, organizationId],
    enabled: (!!organizationId || !!selectedAccountId) && !isSuperAdmin && !isAgency,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const result = await dashboardApi.getAiCredits(selectedAccountId, organizationId);
      return result.data;
    },
  });

  const aiCreditsAvailable = Number(headerStats?.aiCredits?.available ?? 0);
  const aiCreditsTotal = Number(headerStats?.aiCredits?.total ?? 0);
  const aiCreditsDisplayValue = aiCreditsAvailable < 0.3 ? 0 : aiCreditsAvailable;

  const status = currentSubscription?.status || 'none';
  const planName = currentSubscription?.plan?.name || headerStats?.aiCredits?.plan || 'No Plan';
  const trialExpired = isTrialActuallyExpired(currentSubscription);
  const isTrialing = status === 'trialing' && !trialExpired;
  const isActive = status === 'active';
  const isPending = status === 'pending';
  const isNoPlan = !isActive && !isTrialing && !isPending;
  const trialEndsAt = currentSubscription?.trial_end ? new Date(currentSubscription.trial_end) : null;
  const trialDaysRemaining =
    isTrialing && trialEndsAt
      ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : null;
  const dotColor = isActive ? 'bg-green-500' : isTrialing ? 'bg-orange-500' : isPending ? 'bg-red-500' : 'bg-amber-500';

  const filledSegments = useMemo(() => {
    if (!(aiCreditsTotal > 0)) return 0;
    return Math.max(0, Math.min(METER_SEGMENTS, Math.round((aiCreditsDisplayValue / aiCreditsTotal) * METER_SEGMENTS)));
  }, [aiCreditsDisplayValue, aiCreditsTotal]);

  const planOnClick = () => {
    interceptedNavigate(isNoPlan ? ACCOUNT_PLANS_URL : ACCOUNT_BILLING_URL);
  };

  const upgradeOnClick = () => {
    interceptedNavigate(ACCOUNT_PLANS_URL);
  };

  const creditsPanelBody = (
    <div className="px-3 pb-3 pt-2.5">
      <p className="text-[11px] font-medium text-[#94A3B8]">≈ Approx API calls with your credits</p>
      <p className="mt-0.5 text-[13.5px] font-semibold text-white tabular-nums">
        {aiCreditsDisplayValue.toFixed(2)} Credits available
      </p>

      <div className="mt-2.5 max-h-[236px] space-y-[3px] overflow-y-auto">
        {AI_CREDITS_DATA.map((row) => (
          <div
            key={row.api}
            className="flex items-center justify-between gap-3 rounded-md bg-white/[0.06] px-2.5 py-1.5"
          >
            <span className="min-w-0 truncate text-[12px] text-[#E2E8F0]">{row.api}</span>
            <span className="shrink-0 text-[12px] text-[#94A3B8] tabular-nums">
              <span className="font-semibold text-white">{row.costPerCall.toFixed(3)}</span> /call
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const creditsTooltip = (side: 'bottom' | 'right', align: 'center' | 'end') => (
    <TooltipPortal>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={10}
        collisionPadding={12}
        arrowPadding={16}
        className={cn(
          HOVER_PANEL_SURFACE,
          'w-[264px] max-w-[calc(100vw-24px)] overflow-visible p-0 shadow-xl'
        )}
      >
        <TooltipArrow width={12} height={6} className="fill-[#0F172A]" />

        {creditsPanelBody}
      </TooltipContent>
    </TooltipPortal>
  );

  const withCreditsPanel = (
    trigger: React.ReactElement,
    side: 'bottom' | 'right',
    align: 'center' | 'end',
    mobileSide: 'top' | 'bottom',
  ) =>
    isMobile ? (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          side={mobileSide}
          align="end"
          sideOffset={10}
          collisionPadding={12}
          arrowPadding={16}
          className={cn(
            HOVER_PANEL_SURFACE,
            'w-[264px] max-w-[calc(100vw-24px)] overflow-visible p-0 shadow-xl'
          )}
        >
          <PopoverArrow width={12} height={6} className="fill-[#0F172A]" />
          {creditsPanelBody}
        </PopoverContent>
      </Popover>
    ) : (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          {creditsTooltip(side, align)}
        </Tooltip>
      </TooltipProvider>
    );

  if (subscriptionLoading) {
    return (
      <div
        aria-busy="true"
        aria-label="Loading plan"
        className={cn(
          variant === 'card' ? 'wenext-shimmer h-[148px] w-full rounded-xl' : 'wenext-shimmer h-10 w-48 rounded-lg',
          className
        )}
      />
    );
  }

  if (variant === 'card') {
    const displayPlanName = isNoPlan ? 'No Plan' : `${planName}${/plan$/i.test(planName) ? '' : ' Plan'}`;

    return (
      <div
        className={cn(
          'rounded-xl px-3 py-3 space-y-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] border border-[#E4E7EC] bg-white',
          className
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Award className="w-4 h-4 shrink-0 text-[#667085]" strokeWidth={1.8} />
          <span className="text-[12.5px] font-semibold truncate flex-1 min-w-0 text-[#344054]">
            {displayPlanName}
          </span>
          {withCreditsPanel(
            <button
              type="button"
              aria-label="Credit usage details"
              className="w-4 h-4 text-[#98A2B3] hover:text-[#667085] transition outline-none shrink-0"
            >
              <CircleHelp className="w-4 h-4" strokeWidth={1.8} />
            </button>,
            'right',
            'end',
            'top',
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-[#667085]">Credits</span>
            <span className="text-[12px] font-semibold text-[#101828] tabular-nums">
              {aiCreditsLoading ? '—' : formatCredits(aiCreditsDisplayValue)}
            </span>
          </div>
          <div
            className="flex items-stretch gap-[2px] h-4"
            role="meter"
            aria-label="AI credits remaining"
            aria-valuenow={Math.round(aiCreditsDisplayValue)}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, Math.round(aiCreditsTotal))}
          >
            {Array.from({ length: METER_SEGMENTS }).map((_, i) => {
              const filled = i < filledSegments;
              return (
                <span
                  key={i}
                  className={cn('h-full flex-1 rounded-full', !filled && 'bg-[#E4E7EC]')}
                  style={filled ? filledSegmentStyle(i, filledSegments) : undefined}
                />
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={upgradeOnClick}
          className="inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg transition select-none h-9 px-3.5 text-[13px] border border-[#101828] text-[#101828] bg-white hover:bg-[#F9FAFB] w-full"
        >
          Upgrade
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'inline-flex items-center h-10 rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden',
        className
      )}
    >
      <button
        type="button"
        onClick={planOnClick}
        title={isNoPlan ? 'Upgrade your plan' : 'Manage billing'}
        className={cn(
          'flex items-center gap-1.5 h-full px-2.5 transition-colors',
          isNoPlan
            ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
            : 'hover:bg-slate-50'
        )}
      >
        {isNoPlan ? (
          <>
            <Crown className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="text-[13px] font-semibold whitespace-nowrap">Upgrade</span>
          </>
        ) : (
          <>
            <span className={cn('h-2 w-2 rounded-full flex-shrink-0', dotColor)} />
            <span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap">{planName}</span>
            {isTrialing && trialDaysRemaining !== null && (
              <span className="flex items-center gap-0.5 text-[11px] text-orange-500 whitespace-nowrap">
                <Clock className="h-3 w-3" />
                {trialDaysRemaining}d
              </span>
            )}
            {isPending && <Loader2 className="h-3 w-3 animate-spin text-red-500 flex-shrink-0" />}
          </>
        )}
      </button>

      <div className="h-6 w-px bg-slate-200 flex-shrink-0" />

      {withCreditsPanel(
        <div className="flex items-center gap-1.5 h-full px-2.5 cursor-pointer hover:bg-slate-50 transition-colors">
          {aiCreditsLoading ? (
            <div
              aria-busy="true"
              aria-label="Loading AI credits"
              className="wenext-shimmer h-4 w-16 rounded"
            />
          ) : (
            <>
              <Bot className="h-3.5 w-3.5 text-[var(--secondary-color)] flex-shrink-0" />
              <span className="text-[13px] font-semibold text-slate-700 tabular-nums whitespace-nowrap">
                {aiCreditsDisplayValue.toFixed(2)}
              </span>
            </>
          )}
        </div>,
        'bottom',
        'end',
        'bottom',
      )}
    </div>
  );
}
